import React from "react";
import { Tags, TagOption } from "../tags/tags";
import "./data-grid.scss";

export type DataGridCellType = "text" | "tag" | "custom";

export type DataGridAlign = "left" | "center" | "right";

export interface DataGridRow {
  id: string;
  [field: string]: React.ReactNode;
}

export interface DataGridColumn<Row extends DataGridRow = DataGridRow> {
  /** Field key used to read the cell value from each row. */
  key: string;
  /** Column title shown in the header. */
  header: string;
  /**
   * Which cell renderer to use for this column.
   * - "text": renders the raw value as text (default)
   * - "tag": renders a colored tag, mapping the value via `tagOptions`
   * - "custom": renders whatever `render` returns
   * @default "text"
   */
  type?: DataGridCellType;
  /** Fixed column width (e.g. "12.5rem" / "200px"). Omit to flex-fill remaining space. */
  width?: string;
  /** Minimum width applied to a flex-filling column. */
  minWidth?: string;
  /** Horizontal alignment of the header and cell content. @default "left" */
  align?: DataGridAlign;
  /** Truncate overflowing text with an ellipsis. Only applies to "text" cells. */
  truncate?: boolean;
  /**
   * Value-to-tag lookup used when `type` is "tag". Each entry provides the
   * label and styling (background, text color, border) rendered for a value.
   * When no entry matches the cell value, the raw value is shown using
   * `fallbackTagStyle`.
   */
  tagOptions?: TagOption[];
  /** Styling used for a "tag" cell whose value has no matching `tagOptions` entry. */
  fallbackTagStyle?: Pick<TagOption, "backgroundColor" | "textColor" | "showBorder">;
  /** Custom cell renderer. Required when `type` is "custom". */
  render?: (value: React.ReactNode, row: Row) => React.ReactNode;
}

export interface DataGridProps<Row extends DataGridRow = DataGridRow> {
  columns: DataGridColumn<Row>[];
  rows: Row[];
  /** Hide the header row. @default false */
  hideHeader?: boolean;
  /** Content shown when `rows` is empty. */
  emptyState?: React.ReactNode;
  /** Called when a row is clicked. Makes rows interactive when provided. */
  onRowClick?: (row: Row) => void;
  className?: string;
}

const cls = "data-grid";

const DEFAULT_FALLBACK_TAG_STYLE: Pick<
  TagOption,
  "backgroundColor" | "textColor" | "showBorder"
> = {
  backgroundColor: "#f5f5f5",
  textColor: "#5c5c5c",
  showBorder: false,
};

function getColumnStyle<Row extends DataGridRow>(
  column: DataGridColumn<Row>
): React.CSSProperties {
  if (column.width) {
    return { width: column.width, flexShrink: 0 };
  }
  return { flex: "1 1 0%", minWidth: column.minWidth ?? "0" };
}

function renderCellContent<Row extends DataGridRow>(
  column: DataGridColumn<Row>,
  row: Row
): React.ReactNode {
  const value = row[column.key];

  switch (column.type) {
    case "custom":
      return column.render ? column.render(value, row) : value;

    case "tag": {
      const stringValue = value == null ? "" : String(value);
      const matched = column.tagOptions?.find(
        (opt) => opt.value === stringValue
      );
      const fallback = column.fallbackTagStyle ?? DEFAULT_FALLBACK_TAG_STYLE;
      return (
        <Tags
          label={matched?.label ?? stringValue}
          backgroundColor={matched?.backgroundColor ?? fallback.backgroundColor}
          textColor={matched?.textColor ?? fallback.textColor}
          showBorder={matched?.showBorder ?? fallback.showBorder}
        />
      );
    }

    case "text":
    default:
      return (
        <span
          className={`${cls}__text${
            column.truncate ? ` ${cls}__text--truncate` : ""
          }`}
          title={
            column.truncate && typeof value === "string" ? value : undefined
          }
        >
          {value}
        </span>
      );
  }
}

export function DataGrid<Row extends DataGridRow = DataGridRow>({
  columns,
  rows,
  hideHeader = false,
  emptyState,
  onRowClick,
  className,
}: DataGridProps<Row>) {
  const isInteractive = Boolean(onRowClick);

  return (
    <div
      className={[cls, className].filter(Boolean).join(" ")}
      role="table"
    >
      {!hideHeader && (
        <div className={`${cls}__header`} role="row">
          {columns.map((column) => (
            <div
              key={column.key}
              className={`${cls}__header-cell ${cls}__align-${
                column.align ?? "left"
              }`}
              style={getColumnStyle(column)}
              role="columnheader"
            >
              {column.header}
            </div>
          ))}
        </div>
      )}

      <div className={`${cls}__body`}>
        {rows.length === 0 && emptyState != null ? (
          <div className={`${cls}__empty`} role="row">
            {emptyState}
          </div>
        ) : (
          rows.map((row) => (
            <div
              key={row.id}
              className={`${cls}__row${
                isInteractive ? ` ${cls}__row--interactive` : ""
              }`}
              role="row"
              onClick={isInteractive ? () => onRowClick?.(row) : undefined}
              onKeyDown={
                isInteractive
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onRowClick?.(row);
                      }
                    }
                  : undefined
              }
              tabIndex={isInteractive ? 0 : undefined}
            >
              {columns.map((column) => (
                <div
                  key={column.key}
                  className={`${cls}__cell ${cls}__align-${
                    column.align ?? "left"
                  }`}
                  style={getColumnStyle(column)}
                  role="cell"
                >
                  {renderCellContent(column, row)}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default DataGrid;
